// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import {
  Timeline,
  TimelineConnector,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, List, ListItemButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { ConfirmModal, Loading } from '~/components/common';
import { CloneQuoteForm, ConvertOrderFormModal, FormPaymentQuote } from '~/components/module/quotes';
// Others
import { createPayment } from '~/thunks/payment/paymentThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { changeStatusQuote, convertQuoteToOrder } from '~/thunks/quote/quoteThunk';
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import {
  RoleEnum,
  QuoteStatusEnum,
  PaymentTermsEnum,
  BusinessTypeEnum,
  PaymentOrderStatusEnum,
} from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { quoteActions } from '~/thunks/quote/quoteSlice';
import { IBodyChangeStatusQuote, IPaymentQuote, IQuoteDetail } from '~/utils/interface/quotes';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './TimeLineActionQuote.module.scss';
import { icons } from '~/assets';

type Props = {
  onMakePayment: () => void;
  onSendEmail: () => void;
  onPrintQuote: () => void;
  handleShowAllPaymentModal: () => void;
  detailQuote?: IQuoteDetail;
};

const cx = classNames.bind(styles);

const TimeLineActionQuote = (props: Props) => {
  //#region Destructuring Props
  const { onMakePayment, onPrintQuote, handleShowAllPaymentModal, onSendEmail, detailQuote } = props;
  const { id: quoteId } = useParams();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowPaymentModal, setIsShowPaymentModal] = useState<boolean>(false);
  const [isShowConfirmConvertToOrder, setIsShowConfirmConvertToOrder] = useState<boolean>(false);
  const [isShowConfirmChangeStatus, setIsShowConfirmChangeStatus] = useState<boolean>(false);
  const [statusChange, setStatusChange] = useState<string>(EMPTY_STRING);
  const [isShowCloneQuote, setIsShowCloneQuote] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Value
  const isFullPrepayment = detailQuote?.paymentTerms === PaymentTermsEnum.FULL_PREPAYMENT;
  const requiredAmount = isFullPrepayment ? Number(detailQuote?.total) : Number(detailQuote?.depositMadeAmount);
  const isEnoughPayment = Number(detailQuote?.amountPaid) >= requiredAmount;
  //#endregion Declare Value

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowConfirmConvertToOrder = () => {
    setIsShowConfirmConvertToOrder(!isShowConfirmConvertToOrder);
  };

  const handleShowConfirmChangeStatus = (status: string) => {
    setIsShowConfirmChangeStatus(true);
    setStatusChange(status);
  };

  const handleCloseConfirmChangeStatus = () => {
    setIsShowConfirmChangeStatus(false);
    setStatusChange(EMPTY_STRING);
  };

  const handleChangeStatus = () => {
    if (!quoteId) return;

    setIsLoading(true);

    const body: IBodyChangeStatusQuote = { quoteId, status: statusChange };

    dispatch(changeStatusQuote(body))
      .unwrap()
      .then(() => {
        dispatch(quoteActions.setRefreshQuoteDetail(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmChangeStatus();
      });
  };

  const handleConvertToOrder = () => {
    if (!quoteId) return;

    setIsLoading(true);

    dispatch(convertQuoteToOrder(quoteId))
      .unwrap()
      .then((res) => {
        handleNavigateToOrder();
      })
      .catch(() => {})
      .finally(() => {
        handleShowConfirmConvertToOrder();
        setIsLoading(false);
      });
  };

  const handleSavePayment = async (body: IPaymentQuote) => {
    setIsLoading(true);

    const convertData: IPaymentQuote = {
      ...body,
      businessId: quoteId,
      businessType: BusinessTypeEnum.QUOTE,
    };

    dispatch(createPayment(convertData))
      .unwrap()
      .then((_res) => {
        dispatch(quoteActions.setRefreshQuoteDetail(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
        handleClosePaymentModal();
      });
  };

  const handleNavigateToOrder = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.order);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.order);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.order);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.order);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.order);
        break;
    }
  };

  const handleShowCloneQuote = () => {
    setIsShowCloneQuote(!isShowCloneQuote);
  };

  const handleShowPaymentModal = () => {
    handleShowConfirmConvertToOrder();
    setIsShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
  };

  const handleCloneQuote = () => {
    handleShowCloneQuote();
  };
  //#endregion Handle Function

  return (
    <Box id='totalInfoQuoteDetailComponent' className={cx('container')}>
      <Timeline className={cx('timeline')}>
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color='primary' />
            <TimelineConnector />
          </TimelineSeparator>

          <TimelineOppositeContent className={cx('timelineOppositeContent')}>
            <Stack direction={'column'} spacing={4}>
              <Typography className={cx('title')}>{t('quote_detail_action_timeline_title_quote')}</Typography>
              <Button variant='contained' color='success' fullWidth onClick={handleShowConfirmConvertToOrder}>
                {t('quote_detail_action_button_convert_to_order')}
              </Button>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconPaymentFee} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>
                  {t('quote_detail_action_timeline_title_payment_and_fees')}
                </Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                {detailQuote?.paymentStatus !== PaymentOrderStatusEnum.PAID && (
                  <ListItemButton className={cx('action')} onClick={onMakePayment}>
                    {t('quote_detail_action_button_collect_payment')}
                  </ListItemButton>
                )}

                <ListItemButton className={cx('action')} onClick={handleShowAllPaymentModal}>
                  {t('quote_detail_action_button_all_payment')}
                </ListItemButton>
              </List>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconSetting} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>
                  {t('quote_detail_action_timeline_title_quote_actions')}
                </Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                <ListItemButton
                  className={cx('action')}
                  onClick={() => handleShowConfirmChangeStatus(QuoteStatusEnum.QUOTE_EXPIRED)}
                >
                  {t('quote_detail_action_button_expire')}
                </ListItemButton>
                <ListItemButton
                  className={cx('action')}
                  onClick={() => handleShowConfirmChangeStatus(QuoteStatusEnum.QUOTE_CANCELLED)}
                >
                  {t('quote_detail_action_button_cancel')}
                </ListItemButton>
                <ListItemButton className={cx('action')} onClick={handleCloneQuote}>
                  {t('quote_detail_action_button_clone')}
                </ListItemButton>
              </List>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconEmail} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>{t('quote_detail_timeline_title_email')}</Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                <ListItemButton className={cx('action')} onClick={onSendEmail}>
                  {t('quote_detail_timeline_button_send_email')}
                </ListItemButton>
              </List>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconPrint} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>{t('quote_detail_timeline_title_print')}</Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                <ListItemButton className={cx('action')} onClick={onPrintQuote}>
                  {t('quote_detail_timeline_button_print_quote')}
                </ListItemButton>
              </List>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>
      </Timeline>

      {isShowConfirmChangeStatus && (
        <ConfirmModal
          type='default'
          icon={icons.commonIconRepeatBusiness}
          title={t('confirm_change_status')}
          textConfirm={t('common_action_save', { ns: 'common/base' })}
          onClose={handleCloseConfirmChangeStatus}
          onConfirm={handleChangeStatus}
        />
      )}

      {isShowConfirmConvertToOrder && !isEnoughPayment && (
        <ConvertOrderFormModal
          detailQuote={detailQuote}
          onClose={handleShowConfirmConvertToOrder}
          onShowModal={handleShowPaymentModal}
        />
      )}

      {isShowConfirmConvertToOrder && isEnoughPayment && (
        <ConfirmModal
          type='default'
          icon={icons.menuIconOrderInactive}
          title={t('confirm_convert_to_order')}
          textConfirm={t('common_action_convert', { ns: 'common/base' })}
          onClose={handleShowConfirmConvertToOrder}
          onConfirm={handleConvertToOrder}
        />
      )}

      {isShowPaymentModal && detailQuote && (
        <FormPaymentQuote quoteDetail={detailQuote} onClose={handleClosePaymentModal} onSubmit={handleSavePayment} />
      )}

      {isShowCloneQuote && <CloneQuoteForm onClose={handleShowCloneQuote} />}

      {isLoading && <Loading />}
    </Box>
  );
};

export default TimeLineActionQuote;
